import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { getNextPhotoAssetPageCursor } from '@/lib/services/photo-assets/photo-assets.query'
import type { StockPhotoAssetIndexFilters } from '@/lib/services/photo-assets/photo-assets.types'
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  createStockImage,
  createStockImageTag,
  deleteStockImage,
  deleteStockImageTag,
  getStockImage,
  getStockImages,
  getStockImageTags,
  updateStockImage,
  updateStockImageTag,
  type StockImageIndexQueryKeyEntries,
  type StockImageOrganizationScope,
  type StockImageShowQueryKeyEntries,
} from './stock-images.api'
import type {
  CreateStockImageInput,
  CreateStockImageTagInput,
  StockImageIdInput,
  UpdateStockImageInput,
  UpdateStockImageTagInput,
} from './stock-images.types'

export const STOCK_IMAGES_PAGE_SIZE = 40

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface StockImageIndexScope extends OrganizationScope {
  filters?: StockPhotoAssetIndexFilters | undefined
  pageSize?: number | undefined
}

interface StockImageKeyScope extends OrganizationScope {
  stockImageId?: StockImageIdInput | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type StockImageIndexQueryKey = [AdminQueryRoot & StockImageIndexQueryKeyEntries]
type StockImageShowQueryKey = [AdminQueryRoot & StockImageShowQueryKeyEntries]
type StockImageTagsQueryKey = [AdminQueryRoot & StockImageOrganizationScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const stockImagesQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'stock-images' }, { organizationId }),
  ],
  index: ({
    organizationId,
    filters,
    pageSize = STOCK_IMAGES_PAGE_SIZE,
  }: StockImageIndexScope = {}): StockImageIndexQueryKey =>
    buildScopedAdminQueryKey(stockImagesQueryKeys.all({ organizationId })[0], {
      organizationId,
      filters,
      scope: 'index',
      pageSize,
    }),
  show: ({ organizationId, stockImageId }: StockImageKeyScope = {}): StockImageShowQueryKey =>
    buildScopedAdminQueryKey(stockImagesQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      stockImageId: Number(stockImageId),
    }),
  tags: ({ organizationId }: OrganizationScope = {}): StockImageTagsQueryKey =>
    buildScopedAdminQueryKey(stockImagesQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'tags',
    }),
}

export const stockImagesQueries = {
  index: ({
    organizationId,
    filters,
    pageSize = STOCK_IMAGES_PAGE_SIZE,
  }: StockImageIndexScope = {}) =>
    infiniteQueryOptions({
      queryKey: stockImagesQueryKeys.index({ organizationId, filters, pageSize }),
      queryFn: getStockImages,
      initialPageParam: null,
      getNextPageParam: getNextPhotoAssetPageCursor,
    }),
  show: ({ organizationId, stockImageId }: StockImageKeyScope = {}) =>
    queryOptions({
      queryKey: stockImagesQueryKeys.show({ organizationId, stockImageId }),
      queryFn: getStockImage,
    }),
  tags: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: stockImagesQueryKeys.tags({ organizationId }),
      queryFn: getStockImageTags,
    }),
}

export function useStockImages({
  organizationId,
  filters,
  pageSize = STOCK_IMAGES_PAGE_SIZE,
  ...params
}: StockImageIndexScope & QueryParams<ReturnType<typeof stockImagesQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useInfiniteQuery({
    ...stockImagesQueries.index({ organizationId: resolvedOrganizationId, filters, pageSize }),
    ...params,
  })
}

export function useStockImage({
  organizationId,
  stockImageId,
  ...params
}: StockImageKeyScope & QueryParams<ReturnType<typeof stockImagesQueries.show>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...stockImagesQueries.show({ organizationId: resolvedOrganizationId, stockImageId }),
    ...params,
  })
}

export function useStockImageTags({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof stockImagesQueries.tags>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...stockImagesQueries.tags({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useCreateStockImageMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: CreateStockImageInput) => createStockImage({ organizationId, values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: stockImagesQueryKeys.all({ organizationId }) }),
  })
}

export function useUpdateStockImageMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      stockImageId,
      values,
    }: {
      stockImageId: StockImageIdInput
      values: UpdateStockImageInput
    }) => updateStockImage({ organizationId, stockImageId, values }),
    onSuccess: (_response, variables) => {
      void queryClient.invalidateQueries({ queryKey: stockImagesQueryKeys.all({ organizationId }) })
      void queryClient.invalidateQueries({
        queryKey: stockImagesQueryKeys.show({
          organizationId,
          stockImageId: variables.stockImageId,
        }),
      })
    },
  })
}

export function useDeleteStockImageMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (stockImageId: StockImageIdInput) =>
      deleteStockImage({ organizationId, stockImageId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: stockImagesQueryKeys.all({ organizationId }) }),
  })
}

export function useCreateStockImageTagMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: CreateStockImageTagInput) =>
      createStockImageTag({ organizationId, values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: stockImagesQueryKeys.all({ organizationId }) }),
  })
}

export function useUpdateStockImageTagMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tagId, values }: { tagId: number; values: UpdateStockImageTagInput }) =>
      updateStockImageTag({ organizationId, tagId, values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: stockImagesQueryKeys.all({ organizationId }) }),
  })
}

export function useDeleteStockImageTagMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tagId: number) => deleteStockImageTag({ organizationId, tagId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: stockImagesQueryKeys.all({ organizationId }) }),
  })
}
